import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TeamCreate } from "../../types/team.types";
import { TeamsAPI } from "../../APIs/teams.api";
import { useToast } from "../../components/toast";
import { useAuth } from "../../context/AuthContext";
import { TeamForm } from "../../components/team-form";
import bgCreateTeam from "../../assets/bgCreateTeam.jpg";
import { PlusCircle } from "lucide-react";

export default function CreateTeamForm() {
  const navigate = useNavigate();
  const [teamInfo, setTeamInfo] = useState<TeamCreate>({
    name: "",
    institution_id: undefined,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();
  const { updateTokens, user } = useAuth();

  if (user?.teamId) {
    navigate("/dashboard/my-team", { replace: true });
    return null;
  }

  const handleTeamInfoChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;
    setTeamInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamInfo.name || !teamInfo.institution_id) {
      setError("Por favor, rellene todos los campos requeridos");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await TeamsAPI.createTeam(teamInfo);
      if (response.statusCode === 201) {
        addToast({
          message: "¡Equipo creado!",
          type: "success",
          location: "bottom-center",
        });

        updateTokens(response.data.accessToken, response.data.refreshToken);
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 50);
      } else {
        setError(response.message || "Team creation failed");
      }
    } catch (err) {
      setError("Error al crear el equipo");
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToRequestForm = () => {
    navigate("/dashboard/request-institution", { replace: true });
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-cover bg-center relative bg-fixed overflow-hidden"
      style={{ backgroundImage: `url(${bgCreateTeam})` }}
    >
      <div className="absolute inset-0 bg-black opacity-50 z-0" />

      <div className="relative w-full sm:max-w-lg lg:max-w-xl p-8 bg-[#F8F9FA] shadow-xl rounded-2xl bg-opacity-80 text-gray-900 border border-gray-300 z-10 my-4">
        <div className="flex items-center space-x-2 mb-6 justify-center">
          <PlusCircle className="h-6 w-6 text-gray-800" />
          <h1 className="text-2xl font-bold text-gray-800">Crear Equipo</h1>
        </div>
        <TeamForm
          onSubmit={handleSubmit}
          buttonText="Crear"
          isSubmitting={isLoading}
          onRequestNewInstitution={navigateToRequestForm}
          handleChange={handleTeamInfoChange}
          createTeam={teamInfo}
        />
      </div>
    </div>
  );
}
